/**
 * OrganisationTree — expandable hierarchy for the Organisation module.
 * Grouped by branch (Global Ministry → Country → Province → Assembly → …),
 * every row shows rolled-up assembly + member counts. Rows are draggable:
 * drop one organisation onto another to re-parent it (server validates).
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api, { getErrorMessage } from '@/lib/api';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  ChevronRight, ChevronDown, Users, Church, Plus, Pencil, MoreVertical, Coins, UserCheck,
  Archive, ExternalLink, GripVertical, Globe2, Flag, Landmark, MapPin, Home,
  GraduationCap, HeartHandshake, Building2, ArrowUpRight, ShieldCheck, Trophy,
} from 'lucide-react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const TYPE_STYLES = {
  global_ministry: { icon: Globe2, chip: 'bg-indigo-100 text-indigo-700 border-indigo-200' },
  country: { icon: Flag, chip: 'bg-sky-100 text-sky-700 border-sky-200' },
  province: { icon: Landmark, chip: 'bg-violet-100 text-violet-700 border-violet-200' },
  district: { icon: MapPin, chip: 'bg-amber-100 text-amber-700 border-amber-200' },
  assembly: { icon: Church, chip: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  campus: { icon: Building2, chip: 'bg-cyan-100 text-cyan-700 border-cyan-200' },
  bible_college_campus: { icon: GraduationCap, chip: 'bg-rose-100 text-rose-700 border-rose-200' },
  ministry: { icon: HeartHandshake, chip: 'bg-teal-100 text-teal-700 border-teal-200' },
  home_group: { icon: Home, chip: 'bg-slate-100 text-slate-700 border-slate-200' },
  cell: { icon: Home, chip: 'bg-slate-100 text-slate-600 border-slate-200' },
};

const Row = ({ node, depth, expanded, toggle, onAddChild, onEdit, onArchive, onOpen, onSetup,
                onPromote, dragId, setDragId, onDrop, dropTarget, setDropTarget }) => {
  const style = TYPE_STYLES[node.organisation_type] || TYPE_STYLES.assembly;
  const Icon = style.icon;
  const isOpen = expanded.has(node.id);
  const hasKids = (node.children || []).length > 0;
  const isDropTarget = dropTarget === node.id && dragId && dragId !== node.id;

  return (
    <>
      <div
        draggable
        onDragStart={(e) => { setDragId(node.id); e.dataTransfer.effectAllowed = 'move'; }}
        onDragEnd={() => { setDragId(null); setDropTarget(null); }}
        onDragOver={(e) => { if (dragId && dragId !== node.id) { e.preventDefault(); setDropTarget(node.id); } }}
        onDragLeave={() => setDropTarget((t) => (t === node.id ? null : t))}
        onDrop={(e) => { e.preventDefault(); onDrop(node.id); }}
        style={{ paddingLeft: `${Math.min(depth, 4) * 18 + 8}px` }}
        className={`group flex flex-wrap items-center gap-x-2 gap-y-1 py-2.5 pr-3 border-b border-slate-100 transition-colors
          ${isDropTarget ? 'bg-indigo-50 ring-1 ring-inset ring-indigo-300' : 'hover:bg-slate-50'}
          ${dragId === node.id ? 'opacity-40' : ''}`}
        data-testid={`org-row-${node.id}`}
      >
        <GripVertical className="h-3.5 w-3.5 text-slate-300 group-hover:text-slate-400 cursor-grab shrink-0" />

        {hasKids ? (
          <button
            onClick={() => toggle(node.id)}
            className="p-0.5 rounded hover:bg-slate-200 shrink-0"
            data-testid={`org-toggle-${node.id}`}
            aria-label={isOpen ? 'Collapse' : 'Expand'}
          >
            {isOpen ? <ChevronDown className="h-4 w-4 text-slate-500" />
                    : <ChevronRight className="h-4 w-4 text-slate-500" />}
          </button>
        ) : <span className="w-5 shrink-0" />}

        <Icon className="h-4 w-4 text-slate-400 shrink-0" />

        {node.leader_photo_url ? (
          <img src={node.leader_photo_url} alt={node.leader_name || 'Leader'}
               title={`${node.leader_title}: ${node.leader_name}`}
               data-testid={`org-leader-photo-${node.id}`}
               className="h-6 w-6 rounded-full object-cover ring-1 ring-slate-200 shrink-0" />
        ) : node.leader_name ? (
          <span title={`${node.leader_title}: ${node.leader_name}`}
                data-testid={`org-leader-initials-${node.id}`}
                className="h-6 w-6 rounded-full bg-slate-200 text-slate-600 text-[10px] font-semibold grid place-items-center shrink-0">
            {node.leader_name.split(' ').map((w) => w[0]).slice(0, 2).join('')}
          </span>
        ) : null}

        <button
          onClick={() => onOpen(node)}
          className="text-sm font-medium text-slate-900 hover:text-indigo-600 text-left truncate"
          data-testid={`org-name-${node.id}`}
        >
          {node.name}
        </button>

        <Badge variant="outline" className={`text-[10px] shrink-0 ${style.chip}`}>
          {node.type_label}
        </Badge>
        {node.is_church_plant && (
          <Badge variant="outline" className="text-[10px] shrink-0 bg-lime-50 text-lime-700 border-lime-200"
                 data-testid={`org-plant-${node.id}`}>
            Church plant
          </Badge>
        )}
        {node.organisation_type === 'country' && node.control_mode && (
          <Badge variant="outline"
                 className={`text-[10px] shrink-0 ${node.control_mode === 'independent'
                   ? 'bg-indigo-50 text-indigo-700 border-indigo-200'
                   : 'bg-violet-50 text-violet-700 border-violet-200'}`}
                 title={node.control_mode === 'independent'
                   ? `Independent — reports to ${node.parent_name || 'HQ'}`
                   : `Under Provincial Control — managed by ${node.parent_name || 'its Province'}`}
                 data-testid={`org-control-mode-${node.id}`}>
            {node.control_mode === 'independent' ? 'Independent' : 'Under Provincial Control'}
          </Badge>
        )}
        {node.parent_name && (
          <span className="text-[10px] text-slate-400 shrink-0 hidden xl:inline"
                data-testid={`org-parent-${node.id}`}>
            under {node.parent_name}
          </span>
        )}
        {node.code && (
          <span className="text-[10px] font-mono text-slate-400 shrink-0">{node.code}</span>
        )}
        {node.leader_name && (
          <span className="text-[11px] text-slate-500 truncate hidden xl:inline"
                data-testid={`org-leader-name-${node.id}`}>
            {node.leader_title}: {node.leader_name}
          </span>
        )}
        {(node.countries || []).length > 1 && (
          <span className="flex items-center gap-1 shrink-0" data-testid={`org-countries-${node.id}`}>
            {node.countries.slice(0, 4).map((c) => (
              <Badge key={c} variant="outline" className="text-[10px] bg-sky-50 text-sky-700 border-sky-200">
                {c}
              </Badge>
            ))}
            {node.countries.length > 4 && (
              <span className="text-[10px] text-slate-400">+{node.countries.length - 4}</span>
            )}
          </span>
        )}
        {node.setup && !node.setup.complete && !node.setup.dismissed && (
          <button onClick={() => onSetup?.(node)} data-testid={`org-setup-chip-${node.id}`}
                  title="Finish setting this assembly up"
                  className="shrink-0 rounded-full bg-amber-50 text-amber-700 border border-amber-200
                             px-2 py-0.5 text-[10px] font-semibold hover:bg-amber-100 transition-colors">
            Setup {node.setup.done}/{node.setup.total}
          </button>
        )}
        {node.status !== 'active' && (
          <Badge variant="outline" className="text-[10px] shrink-0">{node.status}</Badge>
        )}

        <div className="ml-auto flex flex-wrap items-center justify-end gap-x-4 gap-y-1">
          {!['assembly', 'ministry', 'home_group', 'cell'].includes(node.organisation_type) && (
            <span className="hidden sm:flex items-center gap-1 text-xs text-slate-500"
                  data-testid={`org-assemblies-${node.id}`}>
              <Church className="h-3.5 w-3.5" />
              {node.assembly_count}
              <span className="hidden md:inline text-slate-400">
                {node.assembly_count === 1 ? 'assembly' : 'assemblies'}
              </span>
            </span>
          )}
          <span className="hidden lg:flex items-center gap-1 text-xs text-slate-500"
                data-testid={`org-giving-${node.id}`}
                title={`Giving this period (converted). Own currency: ${money(node.giving_local)} ${node.currency}`}>
            <Coins className="h-3.5 w-3.5" />
            {money(node.giving)}
          </span>
          <span className="hidden lg:flex items-center gap-1 text-xs text-slate-500"
                data-testid={`org-attendance-${node.id}`} title="Average attendance per meeting">
            <UserCheck className="h-3.5 w-3.5" />
            {node.attendance_avg || 0}
          </span>
          <span className="flex items-center gap-1 text-xs text-slate-500"
                data-testid={`org-members-${node.id}`}>
            <Users className="h-3.5 w-3.5" />
            {node.member_count}
            <span className="hidden md:inline text-slate-400">
              {node.member_count === 1 ? 'member' : 'members'}
            </span>
          </span>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-7 w-7"
                      data-testid={`org-actions-${node.id}`}>
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onAddChild(node)}
                                data-testid={`org-add-child-${node.id}`}>
                <Plus className="h-4 w-4 mr-2" /> Add child organisation
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onEdit(node)} data-testid={`org-edit-${node.id}`}>
                <Pencil className="h-4 w-4 mr-2" /> Edit
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onOpen(node)}>
                <ExternalLink className="h-4 w-4 mr-2" /> Open workspace
              </DropdownMenuItem>
              {node.organisation_type === 'country' && node.control_mode === 'provincial' && (
                <DropdownMenuItem onClick={() => onPromote(node)}
                                  data-testid={`org-promote-${node.id}`}>
                  <ArrowUpRight className="h-4 w-4 mr-2" /> Promote to Independent
                </DropdownMenuItem>
              )}
              {['global_ministry', 'country', 'province'].includes(node.organisation_type) && (
                <DropdownMenuItem asChild data-testid={`org-leaderboard-${node.id}`}>
                  <Link to={`/admin/organisations/${node.id}/leaderboard`}>
                    <Trophy className="h-4 w-4 mr-2" /> District leaderboard
                  </Link>
                </DropdownMenuItem>
              )}
              <DropdownMenuItem className="text-red-600" onClick={() => onArchive(node)}
                                data-testid={`org-archive-${node.id}`}>
                <Archive className="h-4 w-4 mr-2" /> Archive
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {isOpen && (node.children || []).map((child) => (
        <Row key={child.id} node={child} depth={depth + 1} expanded={expanded} toggle={toggle}
             onAddChild={onAddChild} onEdit={onEdit} onArchive={onArchive} onOpen={onOpen}
             onSetup={onSetup} onPromote={onPromote}
             dragId={dragId} setDragId={setDragId} onDrop={onDrop}
             dropTarget={dropTarget} setDropTarget={setDropTarget} />
      ))}
    </>
  );
};

const PERIODS = [
  { key: 'this_month', label: 'This month' },
  { key: 'last_month', label: 'Last month' },
  { key: 'this_year', label: 'This year' },
  { key: 'all_time', label: 'All time' },
];

const money = (n) => (n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });

export const OrganisationTree = ({ search = '', onAddChild, onEdit, onOpen, onSetup }) => {
  const queryClient = useQueryClient();
  const [period, setPeriod] = useState('this_month');
  const [currency, setCurrency] = useState('USD');
  const [expanded, setExpanded] = useState(new Set());
  const [dragId, setDragId] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);
  const [seeded, setSeeded] = useState(false);
  const [promoting, setPromoting] = useState(null);
  const [confirmName, setConfirmName] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['org-tree', search, period, currency],
    queryFn: async () => {
      const r = await api.get('/admin/organisations/tree',
        { params: { period, reporting_currency: currency, ...(search ? { search } : {}) } });
      return r.data;
    },
  });

  // Expand the first two levels once the tree lands.
  React.useEffect(() => {
    if (!data?.tree || seeded) return;
    const ids = new Set();
    data.tree.forEach((root) => {
      ids.add(root.id);
      (root.children || []).forEach((c) => ids.add(c.id));
    });
    setExpanded(ids);
    setSeeded(true);
  }, [data, seeded]);

  const { data: fx } = useQuery({
    queryKey: ['org-fx-rates'],
    queryFn: async () => (await api.get('/admin/organisations/fx/rates')).data,
  });

  const reparent = useMutation({
    mutationFn: async ({ id, parent_id }) =>
      (await api.put(`/admin/organisations/${id}`, { parent_id })).data,
    onSuccess: (res) => {
      toast.success(`${res.name} moved`);
      queryClient.invalidateQueries({ queryKey: ['org-tree'] });
      queryClient.invalidateQueries({ queryKey: ['admin-churches'] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const promote = useMutation({
    mutationFn: async ({ id, confirm_name }) => (await api.post(
      `/admin/organisations/${id}/promote-to-independent`, { confirm_name })).data,
    onSuccess: (res) => {
      toast.success(`${res.organisation.name} now reports to ${res.new_parent}`);
      queryClient.invalidateQueries({ queryKey: ['organisation-tree'] });
      setPromoting(null);
      setConfirmName('');
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const archive = useMutation({
    mutationFn: async (id) => (await api.post(`/admin/organisations/${id}/archive`)).data,
    onSuccess: () => {
      toast.success('Organisation archived');
      queryClient.invalidateQueries({ queryKey: ['org-tree'] });
      queryClient.invalidateQueries({ queryKey: ['admin-churches'] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const toggle = (id) => setExpanded((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const handleDrop = (targetId) => {
    if (!dragId || dragId === targetId) return;
    reparent.mutate({ id: dragId, parent_id: targetId });
    setDragId(null);
    setDropTarget(null);
  };

  if (isLoading) {
    return (
      <Card><CardContent className="py-6 space-y-3">
        {[1, 2, 3, 4, 5].map((i) => <Skeleton key={i} className="h-8 w-full" />)}
      </CardContent></Card>
    );
  }

  const totals = data?.totals || {};

  // Districts, flattened with their rolled-up figures, for the rollup panel.
  const districts = [];
  const walk = (nodes) => (nodes || []).forEach((n) => {
    if (n.organisation_type === 'district') districts.push(n);
    walk(n.children);
  });
  walk(data?.tree || []);

  const tree = data?.tree || [];

  return (
    <Card data-testid="organisation-tree">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-1 px-4 py-3 border-b bg-slate-50/70 text-xs text-slate-600">
        <span data-testid="org-total-count"><strong className="text-slate-900">{totals.organisations || 0}</strong> organisations</span>
        <span data-testid="org-total-provinces"><strong className="text-slate-900">{totals.provinces || 0}</strong> provinces</span>
        <span data-testid="org-total-districts"><strong className="text-slate-900">{totals.districts || 0}</strong> districts</span>
        <span data-testid="org-total-assemblies"><strong className="text-slate-900">{totals.assemblies || 0}</strong> assemblies</span>
        <span data-testid="org-total-members"><strong className="text-slate-900">{totals.members || 0}</strong> members</span>
        <span data-testid="org-total-giving">
          <strong className="text-slate-900">{money(totals.giving)}</strong> {data?.reporting_currency || 'USD'} giving
        </span>
        <select
          value={currency}
          onChange={(e) => setCurrency(e.target.value)}
          data-testid="org-currency-select"
          className="h-6 rounded border border-slate-200 bg-white px-1.5 text-[11px]"
          title="Convert every branch into one reporting currency"
        >
          {Object.keys(fx?.rates || { USD: 1 }).sort().map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
        <span className="flex items-center gap-1 text-[11px] text-slate-400"
              data-testid="org-fx-freshness"
              title={`Rates auto-refresh daily at 04:15 UTC from ${fx?.live?.source || 'a free rate feed'}. Manual overrides always win.`}>
          {fx?.live?.fetched_at
            ? `rates ${String(fx.live.fetched_at).slice(0, 10)}`
            : 'rates: seed table'}
          <button
            onClick={async () => {
              try {
                const r = await api.post('/admin/organisations/fx/refresh');
                toast.success(`Rates updated · ${r.data.currencies} currencies from ${r.data.source}`);
                queryClient.invalidateQueries({ queryKey: ['org-fx-rates'] });
                queryClient.invalidateQueries({ queryKey: ['org-tree'] });
              } catch (e) {
                toast.error(getErrorMessage(e));
              }
            }}
            data-testid="org-fx-refresh"
            title="Pull today's rates now"
            className="text-indigo-600 hover:underline"
          >
            refresh
          </button>
        </span>
        <div className="ml-auto flex items-center gap-2">
          <span className="text-slate-400 hidden xl:inline">Drag a row onto another to re-parent it</span>
          <div className="flex rounded-md border border-slate-200 overflow-hidden bg-white">
            {PERIODS.map((p) => (
              <button
                key={p.key}
                onClick={() => setPeriod(p.key)}
                data-testid={`org-period-${p.key}`}
                className={`px-2.5 py-1 text-[11px] font-medium transition-colors ${
                  period === p.key ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-50'}`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
      </div>
      {districts.length > 0 && (
        <div className="border-t border-slate-100 px-4 py-3" data-testid="district-rollup-panel">
          <p className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
            District rollup — giving, attendance and membership
          </p>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {districts.map((d) => (
              <div key={d.id} className="rounded-lg border border-amber-200 bg-amber-50/50 px-3 py-2"
                   data-testid={`district-rollup-${d.id}`}>
                <p className="flex items-center gap-1.5 text-sm font-medium text-slate-800">
                  <MapPin className="h-3.5 w-3.5 text-amber-600" /> {d.name}
                </p>
                <p className="text-[11px] text-slate-500">
                  under {d.parent_name || '—'} · {d.assembly_count} assembl{d.assembly_count === 1 ? 'y' : 'ies'}
                </p>
                <div className="mt-1 flex flex-wrap gap-3 text-xs text-slate-600">
                  <span data-testid={`district-giving-${d.id}`}>
                    <Coins className="mr-1 inline h-3 w-3 text-slate-400" />{money(d.giving)}
                  </span>
                  <span data-testid={`district-attendance-${d.id}`}>
                    <UserCheck className="mr-1 inline h-3 w-3 text-slate-400" />{d.attendance_avg || 0} avg
                  </span>
                  <span data-testid={`district-members-${d.id}`}>
                    <Users className="mr-1 inline h-3 w-3 text-slate-400" />{d.member_count} members
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {promoting && (
        <Dialog open onOpenChange={(o) => { if (!o) setPromoting(null); }}>
          <DialogContent data-testid="promote-country-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-indigo-600" /> Promote {promoting.name} to Independent
              </DialogTitle>
              <DialogDescription>
                This moves the office off <strong>{promoting.parent_name || 'its Province'}</strong> and
                attaches it directly to the Global Ministry, re-pointing its overseer chain. Super Admin
                only, and it takes effect immediately.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2">
              <Label>Type <span className="font-semibold">{promoting.name}</span> to confirm</Label>
              <Input value={confirmName} onChange={(e) => setConfirmName(e.target.value)}
                     data-testid="promote-confirm-input" placeholder={promoting.name} />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setPromoting(null)}>Cancel</Button>
              <Button onClick={() => promote.mutate({ id: promoting.id, confirm_name: confirmName })}
                      disabled={promote.isPending
                        || confirmName.trim().toLowerCase() !== promoting.name.trim().toLowerCase()}
                      data-testid="promote-confirm-btn">
                {promote.isPending ? 'Promoting…' : 'Promote to Independent'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      <div className="divide-y-0">
        {tree.length === 0 ? (
          <div className="py-12 text-center text-sm text-slate-500" data-testid="org-tree-empty">
            No organisations match this search.
          </div>
        ) : tree.map((root) => (
          <Row key={root.id} node={root} depth={0} expanded={expanded} toggle={toggle}
               onAddChild={onAddChild} onEdit={onEdit} onOpen={onOpen} onSetup={onSetup}
               onArchive={(n) => archive.mutate(n.id)}
               onPromote={(n) => { setPromoting(n); setConfirmName(''); }}
               dragId={dragId} setDragId={setDragId} onDrop={handleDrop}
               dropTarget={dropTarget} setDropTarget={setDropTarget} />
        ))}
      </div>
    </Card>
  );
};

export default OrganisationTree;
